import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { type AIMateAgentAttributeType } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import { useSelectedSpaceSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useChatStore } from '../chat-store/useChatStore';
import { useConversation } from '../conversation/useConversation';

interface AnalyticsPropertiesUpdaterProps {
	mode?: string;
}

/**
 * This component is responsible for updating the analytics properties
 * whenever there is a change to attributes that are relevant for analytics
 */
export const AnalyticsPropertiesUpdater = ({ mode }: AnalyticsPropertiesUpdaterProps) => {
	const [{ currentConversationId }] = useChatStore();
	const { experienceId, productKey, productHeader, assistanceServiceConfig, inProductExperience } =
		useGlobalConfig();
	const { selectedAgent, conversation } = useConversation();
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();
	const { fullScreen } = useUIConfig();
	const [{ selectedSpace }] = useSelectedSpaceSelector();

	const modeVal = fullScreen ? 'fullscreen' : mode;

	// When the chat is scoped to a Rovo Space, tag every fired event with the
	// spaceId (mirrors the resolution used at message-send time in
	// chat-input-refresh) so "Space MAU" — unique users who initiate a chat in a
	// space — is measurable from the standard AI MAU `initiated`/`viewed` events.
	// Resolves to `undefined` (never `null`) when there is no active space so the
	// attribute is omitted from non-space events, and — because it is always
	// written below — a previously-set spaceId is cleared when the user moves from
	// a space-scoped chat back to a general one.
	const resolvedSpaceId = FeatureGates.getExperimentValue(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	)
		? (selectedSpace?.id ?? conversation?.rovoSpaceId)
		: undefined;

	const agentAttributes: AIMateAgentAttributeType = {
		agentCreatorType: selectedAgent?.creator_type ?? null,
		agentId: selectedAgent?.id ?? null,
		agentVisibility: selectedAgent?.visibility ?? null,
		agentExternalConfigReference: selectedAgent?.external_config_reference ?? null,
		agentActions:
			(fg('rovo_autodev_analytics_track_actions')
				? selectedAgent?.action_config?.map((config) => config.id)
				: []) ?? [],
		agentIsDefault: selectedAgent?.is_default ?? null,
		agentIsVerified: selectedAgent?.is_verified ?? null,
	};

	setAnalyticsPropertiesWithoutRerender({
		...agentAttributes,
		conversationChannelId: currentConversationId,
		experienceId,
		fullScreen,
		mode: modeVal,
		product: productKey,
		productHeader,
		inProductExperience,
		singleInstrumentationID: currentConversationId,
		...(fg('additional-analytics-params-for-sa')
			? {
					assistanceServiceConfigMinionAlias: assistanceServiceConfig?.minionAlias ?? null,
				}
			: {}),
		// Always written (not conditionally spread) so switching away from a space
		// clears the previous spaceId; `undefined` is omitted from event payloads.
		spaceId: resolvedSpaceId,
		// Launch source from the conversation, surfaced on all fired events.
		...(fg('add_launchsource_to_rovo_chat_analytics')
			? { launchSource: conversation?.resultAttributionContext?.launchSource }
			: {}),
		// Mirrored from the conversation, always written for the same reason as `spaceId`: this is
		// what clears the attributes when the chat moves to a conversation that did not come from
		// a 3P prompt (CCINT-8714).
		isThirdPartyPromptConversation: conversation?.thirdPartyPrompt ? true : undefined,
		thirdPartyPromptCohort: conversation?.thirdPartyPrompt?.cohort,
		thirdPartyConnectCount: conversation?.thirdPartyPrompt?.connectCount,
	});

	return null;
};
